import React, { useState } from 'react';
import { 
  ClipboardEdit, 
  LayoutGrid, 
  TrendingUp, 
  Camera, 
  Palette, 
  Scissors, 
  Briefcase, 
  Code, 
  Utensils, 
  Zap, 
  GraduationCap,
  Hammer,
  ShoppingBag,
  UserCircle,
  Package,
  Lock,
  HelpCircle,
  ArrowRight,
  Crown,
  CheckCircle2,
  Circle,
  Compass,
  Boxes,
  Database
} from 'lucide-react';
import { UserProfile } from '../types';
import logo from '../assets/logo-rmax.jpeg';
import InstallPrompt from './InstallPrompt';
import HelpCenterModal from './HelpCenterModal';
import PendingDraftBanner from './PendingDraftBanner';
import { detectNicheCluster } from '../modules/erp/constants/dashboardNicheConfig';

interface Props {
  user: UserProfile;
  isFirstVisit?: boolean;
  quotesCount?: number;
  materialsCount?: number;
  onOpenChat: () => void;
  onOpenDashboard: () => void;
  onOpenProfile: () => void;
  onOpenMaterials: () => void;
  onOpenErpHub?: () => void;
  onOpenPlatformManagement?: () => void;
  onOpenReformaLote?: () => void;
  onOpenTour?: () => void;
  onStartTrial?: () => void;
  onOpenPaywall?: (plan?: 'monthly' | 'annual' | 'specialist' | 'specialist_annual') => void;
  isActivatingTrial?: boolean;
}

const getNicheIcon = (niche: string) => {
  const n = niche.toLowerCase();
  if (n.includes('foto')) return Camera;
  if (n.includes('design') || n.includes('arte')) return Palette;
  if (n.includes('artesanato') || n.includes('costura')) return Scissors;
  if (n.includes('program') || n.includes('dev') || n.includes('tech')) return Code;
  if (n.includes('comida') || n.includes('gastro') || n.includes('doce')) return Utensils;
  if (n.includes('beleza') || n.includes('estética') || n.includes('moda')) return Scissors;
  if (n.includes('aula') || n.includes('educ') || n.includes('ensino')) return GraduationCap;
  if (n.includes('obra') || n.includes('reforma') || n.includes('marcenaria')) return Hammer;
  if (n.includes('loja') || n.includes('venda') || n.includes('comércio')) return ShoppingBag;
  return Briefcase;
};

export default function Home({ 
  user, 
  isFirstVisit, 
  quotesCount = 0,
  materialsCount = 0,
  onOpenChat, 
  onOpenDashboard, 
  onOpenProfile, 
  onOpenMaterials, 
  onOpenErpHub,
  onOpenPlatformManagement,
  onOpenReformaLote,
  onOpenTour,
  onStartTrial,
  onOpenPaywall,
  isActivatingTrial = false 
}: Props) {
  const [isHelpModalOpen, setIsHelpModalOpen] = useState(false);
  const NicheIcon = getNicheIcon(user?.niche || 'Geral');

  const hasQuotes = quotesCount > 0;
  const hasMaterials = materialsCount > 0;
  const hasProfile = Boolean(user?.businessName || user?.phone);

  return (
    <div className="min-h-dvh bg-zinc-50 flex flex-col p-4 sm:p-6 overflow-y-auto justify-between font-sans">
      <header className="pt-1 sm:pt-2">
        <div className="flex items-center justify-between gap-2 mb-3 sm:mb-4">
          <div className="flex items-center gap-2">
            <img 
              src={logo} 
              alt="R-Max" 
              className="h-9 sm:h-10 w-auto rounded-lg shadow-xs"
            />
          </div>
          <div className="group relative flex items-center gap-1.5 bg-zinc-100 px-2.5 py-1 rounded-full border border-zinc-200 cursor-help">
            <NicheIcon className="h-3 w-3 text-zinc-500" />
            <span className="text-[10px] font-bold text-zinc-600 uppercase tracking-wider">{user?.niche || 'Nicho'}</span>
            
            {/* Tooltip */}
            <div className="absolute bottom-full mb-2 left-1/2 -translate-x-1/2 px-2 py-1 bg-zinc-900 text-white text-[10px] font-medium rounded opacity-0 group-hover:opacity-100 transition-opacity pointer-events-none whitespace-nowrap z-50 shadow-xl">
              Nicho: {user?.niche || 'Não definido'}
              <div className="absolute top-full left-1/2 -translate-x-1/2 border-4 border-transparent border-t-zinc-900" />
            </div>
          </div>
        </div>
        <h1 className="text-xl sm:text-2xl font-black text-zinc-900 leading-tight">
          {isFirstVisit ? 'Que bom ter você aqui!' : `Olá, ${user?.ownerName?.split(' ')[0] || 'Usuário'}!`}
        </h1>
        <p className="text-xs text-zinc-500 mt-0.5">
          O que você deseja fazer hoje no R-Max?
        </p>
      </header>

      <div className="flex-1 flex flex-col justify-center max-w-md mx-auto w-full py-3 space-y-3">
        {/* Banner de Orçamento em Edição (Efeito Zeigarnik) */}
        <PendingDraftBanner userId={user?.id} onResume={onOpenChat} />

        {/* Banner Promocional de Teste Grátis 14 Dias na Home */}
        {user?.plan === 'Grátis' && user?.trialUsed === false && onStartTrial && (
          <div className="bg-linear-to-r from-emerald-950 via-zinc-900 to-zinc-900 border border-emerald-500/40 rounded-3xl p-4 text-white shadow-md flex flex-col items-start justify-between gap-3 animate-in fade-in duration-300">
            <div className="flex items-center gap-3">
              <div className="bg-emerald-500/20 p-2.5 rounded-2xl border border-emerald-500/30 text-emerald-400 shrink-0">
                <Zap className="h-5 w-5" />
              </div>
              <div>
                <span className="text-[10px] font-black uppercase tracking-wider text-emerald-400 bg-emerald-950/80 px-2 py-0.5 rounded-full border border-emerald-800/60 inline-block mb-1">
                  14 Dias de Acesso Integral
                </span>
                <h4 className="text-sm font-black text-white leading-tight">
                  Experimente o Plano Especialista Grátis
                </h4>
                <p className="text-[11px] text-zinc-400 mt-0.5 leading-snug">
                  Liberado: CRM com GPS, Agenda de Atendentes, Recibos PIX com QR Code, Ordens de Serviço e Cockpit DRE.
                </p>
              </div>
            </div>

            <div className="w-full flex items-center justify-between gap-2 pt-1 border-t border-zinc-800/80">
              <span className="text-[10px] text-zinc-500 font-medium">Sem necessidade de cartão agora</span>
              <button
                type="button"
                onClick={onStartTrial}
                disabled={isActivatingTrial}
                className="bg-emerald-500 hover:bg-emerald-400 text-zinc-950 font-black px-4 py-2 rounded-xl text-xs flex items-center gap-1.5 transition-all shadow-md shadow-emerald-500/20 active:scale-95 cursor-pointer disabled:opacity-50"
              >
                <span>{isActivatingTrial ? 'Ativando...' : 'Ativar Teste Grátis'}</span>
                <ArrowRight className="h-3.5 w-3.5" />
              </button>
            </div>
          </div>
        )}

        {/* Guia Rápido de Configuração Inicial (Checklist) */}
        {(!hasQuotes || !hasMaterials || !hasProfile) && (
          <div className="bg-white border border-zinc-200/80 rounded-3xl p-4 shadow-xs space-y-3">
            <div className="flex items-center justify-between border-b border-zinc-100 pb-2">
              <span className="text-[10px] font-black tracking-wider text-zinc-400 uppercase">Primeiros Passos</span>
              <span className="text-[10px] font-bold text-brand bg-brand/10 px-2 py-0.5 rounded-full">
                {[hasQuotes, hasMaterials, hasProfile].filter(Boolean).length}/3 Concluídos
              </span>
            </div>

            <div className="space-y-1.5 text-xs">
              <button
                type="button"
                onClick={onOpenChat}
                className="w-full flex items-center justify-between p-2.5 rounded-xl border border-zinc-100 hover:border-zinc-300 hover:bg-zinc-50 transition-colors text-left cursor-pointer group"
              >
                <div className="flex items-center gap-2.5">
                  {hasQuotes ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-zinc-300 shrink-0" />
                  )}
                  <span className={`font-medium ${hasQuotes ? 'text-zinc-500 line-through' : 'text-zinc-800'}`}>
                    1. Gerar seu primeiro orçamento comercial
                  </span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-zinc-400 group-hover:translate-x-0.5 transition-transform" />
              </button>

              <button
                type="button"
                onClick={onOpenMaterials}
                className="w-full flex items-center justify-between p-2.5 rounded-xl border border-zinc-100 hover:border-zinc-300 hover:bg-zinc-50 transition-colors text-left cursor-pointer group"
              >
                <div className="flex items-center gap-2.5">
                  {hasMaterials ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-zinc-300 shrink-0" />
                  )}
                  <span className={`font-medium ${hasMaterials ? 'text-zinc-500 line-through' : 'text-zinc-800'}`}>
                    2. Cadastrar insumos e definir Valor Hora
                  </span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-zinc-400 group-hover:translate-x-0.5 transition-transform" />
              </button>

              <button
                type="button"
                onClick={onOpenProfile}
                className="w-full flex items-center justify-between p-2.5 rounded-xl border border-zinc-100 hover:border-zinc-300 hover:bg-zinc-50 transition-colors text-left cursor-pointer group"
              >
                <div className="flex items-center gap-2.5">
                  {hasProfile ? (
                    <CheckCircle2 className="w-4 h-4 text-emerald-600 shrink-0" />
                  ) : (
                    <Circle className="w-4 h-4 text-zinc-300 shrink-0" />
                  )}
                  <span className={`font-medium ${hasProfile ? 'text-zinc-500 line-through' : 'text-zinc-800'}`}>
                    3. Personalizar Marca, WhatsApp e PIX
                  </span>
                </div>
                <ArrowRight className="w-3.5 h-3.5 text-zinc-400 group-hover:translate-x-0.5 transition-transform" />
              </button>
            </div>
          </div>
        )}

        {/* Grade de 4 Ações Principais do Usuário */}
        <div className="grid grid-cols-2 gap-3 sm:gap-4">
          <button
            onClick={onOpenChat}
            className="group relative bg-white border border-brand/20 hover:border-brand p-5 sm:p-6 rounded-3xl shadow-xs hover:shadow-lg hover:shadow-brand/5 transition-all duration-300 flex flex-col items-center justify-center text-center aspect-square gap-3 cursor-pointer"
          >
            <div className="bg-brand/5 p-3.5 rounded-2xl group-hover:scale-110 transition-transform duration-300">
              <ClipboardEdit className="h-8 sm:h-9 w-8 sm:w-9 text-brand" strokeWidth={1.7} />
            </div>
            <h2 className="text-xs sm:text-sm font-black text-zinc-900 uppercase tracking-tight">Novo Orçamento</h2>
          </button>

          <button
            onClick={onOpenDashboard}
            className="group relative bg-white border border-zinc-100 hover:border-zinc-900 p-5 sm:p-6 rounded-3xl shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col items-center justify-center text-center aspect-square gap-3 cursor-pointer"
          >
            {user?.id === 'guest' && (
              <span className="absolute top-4 right-4 bg-zinc-100 border border-zinc-200 text-zinc-400 p-1.5 rounded-full" title="Cadastro necessário">
                <Lock className="h-3 w-3" />
              </span>
            )}
            <div className="bg-zinc-50 p-3.5 rounded-2xl group-hover:scale-110 transition-transform duration-300">
              <LayoutGrid className="h-8 sm:h-9 w-8 sm:w-9 text-zinc-900" strokeWidth={1.7} />
            </div>
            <h2 className="text-xs sm:text-sm font-black text-zinc-900 uppercase tracking-tight leading-tight">Meu Painel</h2>
          </button>

          <button
            onClick={onOpenMaterials}
            className="group relative bg-white border border-amber-600/10 hover:border-amber-600 p-5 sm:p-6 rounded-3xl shadow-xs hover:shadow-lg hover:shadow-amber-600/5 transition-all duration-300 flex flex-col items-center justify-center text-center aspect-square gap-3 cursor-pointer"
          >
            {user?.id === 'guest' && (
              <span className="absolute top-4 right-4 bg-zinc-100 border border-zinc-200 text-zinc-400 p-1.5 rounded-full" title="Cadastro necessário">
                <Lock className="h-3 w-3" />
              </span>
            )}
            <div className="bg-amber-50 p-3.5 rounded-2xl group-hover:scale-110 transition-transform duration-300">
              <Boxes className="h-8 sm:h-9 w-8 sm:w-9 text-amber-600" strokeWidth={1.7} />
            </div>
            <h2 className="text-xs sm:text-sm font-black text-zinc-900 uppercase tracking-tight whitespace-pre-line leading-tight">Materiais &{"\n"}Insumos</h2>
          </button>

          <button
            onClick={onOpenProfile}
            className="group relative bg-white border border-zinc-100 hover:border-blue-600 p-5 sm:p-6 rounded-3xl shadow-xs hover:shadow-lg transition-all duration-300 flex flex-col items-center justify-center text-center aspect-square gap-3 cursor-pointer"
          >
            {user?.id === 'guest' && (
              <span className="absolute top-4 right-4 bg-zinc-100 border border-zinc-200 text-zinc-400 p-1.5 rounded-full" title="Cadastro necessário">
                <Lock className="h-3 w-3" />
              </span>
            )}
            <div className={`p-3.5 rounded-2xl group-hover:scale-110 transition-transform duration-300 flex items-center justify-center ${!user?.logoUrl ? 'bg-blue-50' : ''}`}>
              {user?.logoUrl ? (
                <img src={user.logoUrl} alt="Logo" className="w-8 sm:w-9 h-8 sm:h-9 rounded-full object-cover aspect-square shadow-xs" />
              ) : (
                <UserCircle className="h-8 sm:h-9 w-8 sm:w-9 text-blue-600" />
              )}
            </div>
            <h2 className="text-xs sm:text-sm font-black text-zinc-900 uppercase tracking-tight">Minha Empresa</h2>
          </button>
        </div>

        {/* Botão de Gestão & Organização do Negócio (Especialista) */}
        {onOpenErpHub && (
          <div className="w-full">
            <button
              onClick={onOpenErpHub}
              className="w-full bg-linear-to-r from-emerald-600 via-emerald-700 to-teal-800 hover:from-emerald-500 hover:to-teal-700 text-white rounded-3xl p-4 shadow-xs hover:shadow-md transition-all duration-300 flex items-center gap-3.5 cursor-pointer text-left group border border-emerald-500/30"
            >
              <div className="bg-white/20 p-2.5 rounded-2xl text-white shrink-0 group-hover:scale-105 transition-transform duration-300">
                <Zap className="h-5 w-5 text-white" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-black text-white leading-tight uppercase tracking-tight">
                    Gestão & Organização do Negócio
                  </h3>
                  <span className="text-[9px] font-black bg-white text-emerald-800 px-2 py-0.5 rounded-full uppercase shadow-xs">
                    {user?.plan === 'Especialista' ? 'Especialista Ativo' : user?.plan === 'Pro' ? 'PRO' : '14 Dias Grátis'}
                  </span>
                </div>
                <p className="text-[10px] text-emerald-100 mt-0.5 leading-normal">
                  {(() => {
                    const cluster = detectNicheCluster(user?.niche, user?.profession);
                    switch (cluster) {
                      case 'care_estetica':
                        return 'Agenda Online, Clientes & Fidelidade, Recibos PIX e Grade do Salão.';
                      case 'campo_obras':
                        return 'Orçamentos, OS com Fotos, Assinatura na Tela e Recibos PIX.';
                      case 'producao_food':
                        return 'Fichas Técnicas, Encomendas, Clientes e Recibos PIX.';
                      case 'projetos_tech':
                        return 'Propostas Comerciais, Clientes, Pipeline de Vendas e Recibos PIX.';
                      default:
                        return 'Clientes, Agenda, Recibos PIX, Orçamentos e Controle Financeiro.';
                    }
                  })()}
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-white/70 group-hover:translate-x-1 transition-transform shrink-0" />
            </button>
          </div>
        )}

        {/* Simulador da Reforma Tributária em Lote (CBS & IBS) */}
        <div className="w-full">
          <button
            type="button"
            onClick={onOpenReformaLote || (() => { window.location.href = '/app?view=reforma_lote'; })}
            className="w-full bg-linear-to-r from-slate-900 via-indigo-950 to-slate-900 hover:from-slate-800 hover:to-indigo-900 text-white rounded-3xl p-4 shadow-sm hover:shadow-md transition-all duration-300 flex items-center gap-3.5 cursor-pointer text-left group border border-indigo-500/30"
          >
            <div className="bg-indigo-500/20 border border-indigo-500/30 p-2.5 rounded-2xl text-indigo-300 shrink-0 group-hover:scale-105 transition-transform duration-300">
              <Database className="h-5 w-5" />
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-xs font-black text-white leading-tight uppercase tracking-tight">
                  Simulador de Reforma em Lote (CBS & IBS)
                </h3>
                <span className="text-[9px] font-black bg-indigo-500 text-white px-2 py-0.5 rounded-full uppercase shadow-xs">
                  Reforma 2026
                </span>
              </div>
              <p className="text-[10px] text-slate-300 mt-0.5 leading-normal">
                Auditoria de entradas e saídas, créditos tributários e recálculo com proteção de margem.
              </p>
            </div>
            <ArrowRight className="h-4 w-4 text-indigo-300 group-hover:translate-x-1 transition-transform shrink-0" />
          </button>
        </div>

        {/* Atalho Exclusivo: Torre de Controle da Diretoria (Admin Only) */}
        {(user?.isAdmin || user?.isMasterUser) && onOpenPlatformManagement && (
          <div className="w-full">
            <button
              onClick={onOpenPlatformManagement}
              className="w-full bg-linear-to-r from-slate-900 via-slate-950 to-zinc-900 hover:from-slate-800 hover:to-zinc-800 text-white rounded-3xl p-4 shadow-lg transition-all duration-300 flex items-center gap-3.5 cursor-pointer text-left group border border-amber-500/40"
            >
              <div className="bg-amber-500/20 border border-amber-500/30 p-2.5 rounded-2xl text-amber-400 shrink-0 group-hover:scale-105 transition-transform duration-300">
                <Crown className="h-5 w-5" />
              </div>
              <div className="flex-1 min-w-0">
                <div className="flex items-center gap-2">
                  <h3 className="text-xs font-black text-amber-400 leading-tight uppercase tracking-tight">
                    Torre de Controle (B.I. da Diretoria)
                  </h3>
                  <span className="text-[9px] font-black bg-amber-500 text-slate-950 px-2 py-0.5 rounded-full uppercase shadow-xs">
                    Admin
                  </span>
                </div>
                <p className="text-[10px] text-slate-400 mt-0.5 leading-normal">
                  Telemetria da base, adesão de planos, orçamentos e monitor LGPD.
                </p>
              </div>
              <ArrowRight className="h-4 w-4 text-amber-400/70 group-hover:translate-x-1 transition-transform shrink-0" />
            </button>
          </div>
        )}

        {/* Atalho Dúvidas Frequentes e Tour Guiado */}
        <div className="grid grid-cols-1 sm:grid-cols-2 gap-2">
          {onOpenTour && (
            <button
              type="button"
              onClick={onOpenTour}
              className="w-full bg-white border border-zinc-200 hover:border-zinc-400 rounded-2xl p-3 shadow-xs transition-all flex items-center gap-3 cursor-pointer text-left group"
            >
              <div className="bg-zinc-100 p-2 rounded-xl text-zinc-700 shrink-0 group-hover:scale-105 transition-transform">
                <Compass className="h-4 w-4" />
              </div>
              <div className="flex-1 min-w-0">
                <h4 className="text-xs font-black text-zinc-900 leading-tight">Tour Guiado</h4>
                <p className="text-[10px] text-zinc-500 truncate">Como funciona o R-Max</p>
              </div>
            </button>
          )}

          <button
            type="button"
            onClick={() => setIsHelpModalOpen(true)}
            className="w-full bg-white border border-zinc-200 hover:border-zinc-400 rounded-2xl p-3 shadow-xs transition-all flex items-center gap-3 cursor-pointer text-left group"
          >
            <div className="bg-brand/10 p-2 rounded-xl text-brand shrink-0 group-hover:scale-105 transition-transform">
              <HelpCircle className="h-4 w-4" />
            </div>
            <div className="flex-1 min-w-0">
              <h4 className="text-xs font-black text-zinc-900 leading-tight">Dúvidas Frequentes</h4>
              <p className="text-[10px] text-zinc-500 truncate">Tutoriais e suporte</p>
            </div>
          </button>
        </div>

        {/* Atalho para Adicionar à Tela Inicial (PWA) */}
        <InstallPrompt />
      </div>

      <HelpCenterModal 
        isOpen={isHelpModalOpen} 
        onClose={() => setIsHelpModalOpen(false)} 
        userNiche={user?.niche || user?.profession}
        userPlan={user?.plan}
      />

      <footer className="pt-2 text-center opacity-40">
        <p className="text-[8px] text-zinc-400 font-black uppercase tracking-[0.3em]">
          Powered by R-Max Intelligence
        </p>
      </footer>
    </div>
  );
}
